import type {
  ExperimentCadence,
  ExperimentControlOption,
  ExperimentDataSource,
} from "~/ui-rtk/types/experiments"

/**
 * Phase-1 (test window) lengths the pipeline's test design accepts, in
 * days. Mirrors `EXPERIMENT_DURATION_OPTIONS_DAYS` in the API; 30 is the
 * pipeline default. Extendable after launch via the experiment update flow.
 */
export const EXPERIMENT_DURATION_OPTIONS = [30, 60, 90, 120, 150, 180] as const

export type ExperimentDurationDays =
  (typeof EXPERIMENT_DURATION_OPTIONS)[number]

export const EXPERIMENT_DEFAULT_DURATION_DAYS: ExperimentDurationDays = 30

/**
 * A ready design whose minimum detectable lift (80% power, 90% CI) for the
 * experiment's own duration exceeds this fraction is flagged as
 * underpowered. Warn-only: the user is told to create a new experiment with
 * a longer window or larger control.
 */
export const EXPERIMENT_UNDERPOWERED_MDE = 0.1

/**
 * Experiments start on Wednesdays, at least this many days out — mirrors
 * the pipeline's `get_next_wednesday(min_days_ahead=7)` default.
 */
export const EXPERIMENT_MIN_DAYS_AHEAD = 7

/**
 * Analysis grain. The pipeline stores everything daily; `weekly` re-sums the
 * days into Monday-start weeks before fitting, which smooths noisy
 * (low-volume or weekly-reported) accounts.
 */
export const EXPERIMENT_CADENCE_OPTIONS: {
  value: ExperimentCadence
  label: string
  description: string
}[] = [
  {
    value: "daily",
    label: "Daily",
    description: "Standard for accounts with steady daily order volume.",
  },
  {
    value: "weekly",
    label: "Weekly",
    description:
      "Smooths noisy or weekly-reported revenue (e.g. wholesale CSV uploads).",
  },
]

/**
 * Revenue source the design is optimized on — the pipeline's dependent
 * variable at design time. Empty = the API derives it from the company's
 * modelling dep_var (CSV-upload experiments default to Shopify, since the
 * uploads land in the Shopify-shaped tables). `wholesale` designs on
 * dbt-backed retailer sell-through revenue: daily-grain sources only unless
 * the cadence is weekly. The analysis still reports every metric (baseline /
 * paid / new / repeat …) regardless of this choice.
 */
export const EXPERIMENT_DATA_SOURCE_OPTIONS: {
  value: ExperimentDataSource | ""
  label: string
}[] = [
  { value: "", label: "Auto (company default)" },
  { value: "shopify", label: "Shopify revenue" },
  { value: "ga", label: "Google Analytics revenue" },
  { value: "wholesale", label: "Wholesale revenue" },
]

/**
 * Control share = percent of REVENUE held out as control (the pipeline's
 * `percent_in_control` is `controlPercentage / 100`), not a count of
 * markets. Any multiple of the step within the bounds is offered — mirrors
 * `EXPERIMENT_CONTROL_SHARE_*` in the API. 10% is the production default for
 * geo-holdout tests.
 */
export const EXPERIMENT_CONTROL_SHARE_STEP = 5
export const EXPERIMENT_CONTROL_SHARE_MIN = 5
export const EXPERIMENT_CONTROL_SHARE_MAX = 95
export const EXPERIMENT_DEFAULT_CONTROL_PERCENTAGE = 10

export const EXPERIMENT_CONTROL_SHARES: readonly number[] = Array.from(
  {
    length:
      (EXPERIMENT_CONTROL_SHARE_MAX - EXPERIMENT_CONTROL_SHARE_MIN) /
        EXPERIMENT_CONTROL_SHARE_STEP +
      1,
  },
  (_, index) =>
    EXPERIMENT_CONTROL_SHARE_MIN + index * EXPERIMENT_CONTROL_SHARE_STEP,
)

export const formatControlSplit = (controlPercentage: number) =>
  `${100 - controlPercentage}% test / ${controlPercentage}% control`

/**
 * Splits with a rationale worth showing. Every other offered share gets a
 * generic description from `describeControlShare`.
 */
export const EXPERIMENT_CONTROL_OPTIONS: ExperimentControlOption[] = [
  {
    id: "control-10",
    controlPercentage: 10,
    testPercentage: 90,
    label: formatControlSplit(10),
    description:
      "Maximum reach while holding out enough revenue to measure lift. Our standard for geo-holdout tests.",
    recommended: true,
  },
  {
    id: "control-20",
    controlPercentage: 20,
    testPercentage: 80,
    label: formatControlSplit(20),
    description:
      "Larger holdout for tighter confidence intervals at the cost of some reach.",
  },
  {
    id: "control-25",
    controlPercentage: 25,
    testPercentage: 75,
    label: formatControlSplit(25),
    description:
      "Three quarters of revenue in test with a sturdier holdout than the standard 10%.",
  },
  {
    id: "control-50",
    controlPercentage: 50,
    testPercentage: 50,
    label: formatControlSplit(50),
    description:
      "Even split for maximum statistical power on smaller accounts.",
  },
  {
    id: "control-75",
    controlPercentage: 75,
    testPercentage: 25,
    label: formatControlSplit(75),
    description:
      "A quarter of revenue in test: the cheapest footprint to run, with a large control. The read is noisier, so check the power estimate against the planned budget.",
  },
]

/** The option for any offered share: a named preset, or a generic description. */
export const describeControlShare = (
  controlPercentage: number,
): ExperimentControlOption => {
  const preset = EXPERIMENT_CONTROL_OPTIONS.find(
    option => option.controlPercentage === controlPercentage,
  )
  if (preset) {
    return preset
  }
  let description: string
  if (controlPercentage < 10) {
    description =
      "Near-total reach, but very little revenue anchors the control — expect a wide confidence interval."
  } else if (controlPercentage < 50) {
    description =
      "Most of revenue gets the campaign; the holdout is larger than the standard 10%, which steadies the read."
  } else {
    description =
      "The campaign runs in a minority of revenue, holding most of it out as control — cheaper to run, noisier to read."
  }
  return {
    id: `control-${controlPercentage}`,
    controlPercentage,
    testPercentage: 100 - controlPercentage,
    label: formatControlSplit(controlPercentage),
    description,
  }
}
